import { useId, useState, type FormEvent } from "react";
import { Plus } from "lucide-react";
import {
  useCreateDirector,
  useDirectors,
  useMe,
  useUpdateDirector,
  type Director,
  type DirectorCreateInput,
} from "@/api/users";
import { RequestError } from "@/components/RequestError";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TextField } from "@/components/ui/field";
import { FormActions } from "@/components/ui/form-actions";
import { formatDate } from "@/lib/format";
import { useFormValues, useSubmitState } from "@/lib/use-form";

const PASSWORD_HINT = "At least 12 characters. They can change it after signing in.";

function NewDirectorForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (input: DirectorCreateInput) => Promise<unknown>;
  onCancel: () => void;
}) {
  const id = useId();
  const { values, set } = useFormValues({ username: "", full_name: "", email: "", password: "" });
  const { error, errors, saving, run } = useSubmitState();

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void run(() =>
      onSubmit({
        username: values.username.trim(),
        full_name: values.full_name.trim(),
        email: values.email.trim() || null,
        password: values.password,
      }),
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      <RequestError error={error} />
      <div className="grid gap-4 md:grid-cols-2">
        <TextField
          id={`${id}-full-name`}
          label="Full name"
          value={values.full_name}
          onChange={(value) => set("full_name", value)}
          error={errors.full_name}
        />
        <TextField
          id={`${id}-username`}
          label="Username"
          autoCapitalize="none"
          autoComplete="off"
          value={values.username}
          onChange={(value) => set("username", value)}
          error={errors.username}
          hint="Lower-case letters, digits, dot, dash or underscore"
        />
        <TextField
          id={`${id}-email`}
          label="E-mail"
          type="email"
          value={values.email}
          onChange={(value) => set("email", value)}
          error={errors.email}
        />
        <TextField
          id={`${id}-password`}
          label="Temporary password"
          type="password"
          autoComplete="new-password"
          value={values.password}
          onChange={(value) => set("password", value)}
          error={errors.password}
          hint={PASSWORD_HINT}
        />
      </div>
      <FormActions saving={saving} submitLabel="Add director" onCancel={onCancel} />
    </form>
  );
}

function ResetPasswordForm({
  director,
  onSubmit,
  onCancel,
}: {
  director: Director;
  onSubmit: (password: string) => Promise<unknown>;
  onCancel: () => void;
}) {
  const id = useId();
  const [password, setPassword] = useState("");
  const { error, errors, saving, run } = useSubmitState();

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void run(() => onSubmit(password));
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-3">
      <RequestError error={error} />
      <TextField
        id={`${id}-password`}
        label={`New password for ${director.full_name}`}
        type="password"
        autoComplete="new-password"
        value={password}
        onChange={setPassword}
        error={errors.password}
        hint={`${PASSWORD_HINT} Resetting also unlocks the account and signs them out everywhere.`}
      />
      <FormActions saving={saving} submitLabel="Reset password" onCancel={onCancel} />
    </form>
  );
}

export function DirectorsPanel() {
  const me = useMe();
  const directors = useDirectors();
  const create = useCreateDirector();
  const update = useUpdateDirector();
  const [adding, setAdding] = useState(false);
  const [resettingId, setResettingId] = useState<string | null>(null);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          Every director has the same access. Deactivating a director signs them out immediately.
        </p>
        {!adding && (
          <Button size="sm" onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" aria-hidden />
            Add director
          </Button>
        )}
      </div>

      {adding && (
        <Card>
          <CardHeader>
            <CardTitle>New director</CardTitle>
          </CardHeader>
          <CardContent>
            <NewDirectorForm
              onSubmit={async (input) => {
                await create.mutateAsync(input);
                setAdding(false);
              }}
              onCancel={() => setAdding(false)}
            />
          </CardContent>
        </Card>
      )}

      <RequestError error={update.error} />

      {directors.isPending ? (
        <p className="text-muted-foreground">Loading directors…</p>
      ) : directors.isError ? (
        <RequestError error={directors.error} />
      ) : (
        <ul className="grid gap-3 md:grid-cols-2">
          {directors.data.map((director) => {
            const isMe = director.id === me.data?.id;
            const locked =
              director.locked_until !== null && new Date(director.locked_until) > new Date();
            return (
              <li key={director.id}>
                <Card className="h-full">
                  <CardContent className="flex flex-col gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold">{director.full_name}</span>
                      {isMe && <Badge>You</Badge>}
                      {!director.is_active && <Badge variant="muted">Deactivated</Badge>}
                      {locked && <Badge variant="warning">Locked</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {director.username}
                      {director.email ? ` · ${director.email}` : ""}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Last sign-in {formatDate(director.last_login_at)}
                    </p>
                    {resettingId === director.id ? (
                      <ResetPasswordForm
                        director={director}
                        onSubmit={async (password) => {
                          await update.mutateAsync({ id: director.id, input: { password } });
                          setResettingId(null);
                        }}
                        onCancel={() => setResettingId(null)}
                      />
                    ) : (
                      !isMe && (
                        <div className="flex flex-wrap gap-2 pt-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setResettingId(director.id)}
                          >
                            Reset password
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={update.isPending}
                            onClick={() =>
                              update.mutate({
                                id: director.id,
                                input: { is_active: !director.is_active },
                              })
                            }
                          >
                            {director.is_active ? "Deactivate" : "Reactivate"}
                          </Button>
                        </div>
                      )
                    )}
                  </CardContent>
                </Card>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
